import { useState } from 'react';
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, Chip, IconButton, Menu, MenuItem, ListItemIcon, ListItemText,
  Stack, Skeleton, Paper, alpha, useTheme, Switch, Tooltip,
  TablePagination, Divider,
} from '@mui/material';
import {
  MoreVert, Edit, Description, CardGiftcard, Block, Delete, LockOpen, VerifiedUser, Inbox, Visibility,
  AccountBalanceWallet,
} from '@mui/icons-material';
import BalanceAdjustPopover from './BalanceAdjustPopover';
import { formatEmail, formatPhone } from '../../utils/contactMask';
import type { PartnerRow } from '../../services/partnerUserService';
import { useI18n } from '../../i18n/I18nProvider';
import { translateUiText } from '../../i18n/translateUiText';
import { formatMoney } from '../../utils/currency';
import { formatRegisteredDate, formatRegisteredTime } from '../../utils/dateFormat';

const STATUS_COLORS: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
  verified: 'success',
  non_verified: 'warning',
  pending_approval: 'info',
  blocked: 'error',
  deleted: 'default',
  expired: 'error',
};

interface PartnerListTableProps {
  rows: PartnerRow[];
  loading?: boolean;
  maskEnabled?: boolean;
  walletLoading?: boolean;
  onEdit: (row: PartnerRow) => void;
  onDocuments: (row: PartnerRow) => void;
  onReferrals: (row: PartnerRow) => void;
  onProfile?: (row: PartnerRow) => void;
  onView?: (row: PartnerRow) => void;
  onBlock: (row: PartnerRow) => void;
  onUnblock: (row: PartnerRow) => void;
  onDelete: (row: PartnerRow) => void;
  onToggleActive: (row: PartnerRow, isActive: boolean) => void;
  onWalletAdjust: (row: PartnerRow, mode: 'credit' | 'debit', amount: number, note: string) => void;
  onWalletView?: (row: PartnerRow) => void;
  onRefresh: () => void;
  totalCount?: number;
  page?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
}

export default function PartnerListTable({
  rows,
  loading,
  maskEnabled,
  walletLoading,
  onEdit,
  onDocuments,
  onReferrals,
  onProfile,
  onView,
  onBlock,
  onUnblock,
  onDelete,
  onToggleActive,
  onWalletAdjust,
  onWalletView,
  onRefresh,
  totalCount,
  page = 0,
  pageSize = 25,
  onPageChange,
  onPageSizeChange,
}: PartnerListTableProps) {
  const theme = useTheme();
  const { t } = useI18n();
  const tl = (s: string) => translateUiText(s, t);
  const serialOffset = page * pageSize;
  const showPagination = totalCount != null && onPageChange && onPageSizeChange;
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuRow, setMenuRow] = useState<PartnerRow | null>(null);

  const openMenu = (e: React.MouseEvent<HTMLElement>, row: PartnerRow) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuRow(row);
  };

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuRow(null);
  };

  if (loading) {
    return (
      <Paper elevation={0} sx={{ borderRadius: 2.5, border: 1, borderColor: 'divider' }}>
        {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} height={52} sx={{ mx: 2, my: 0.5 }} />)}
      </Paper>
    );
  }

  if (!rows.length) {
    return (
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2.5,
          border: 1,
          borderColor: 'divider',
          borderStyle: 'dashed',
          py: 6,
          textAlign: 'center',
          bgcolor: alpha(theme.palette.primary.main, 0.02),
        }}
      >
        <Inbox sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
        <Typography variant="subtitle1" fontWeight={700} color="text.secondary">No partners found</Typography>
        <Typography variant="body2" color="text.disabled">Try adjusting filters or add a new partner</Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={0} sx={{ borderRadius: 2.5, border: 1, borderColor: 'divider', overflow: 'hidden' }}>
      <TableContainer>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper', width: 56 }}>{tl('S.No')}</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>{tl('Partner')}</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper', display: { xs: 'none', sm: 'table-cell' } }}>{tl('Contact')}</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper', display: { xs: 'none', md: 'table-cell' } }}>{tl('Zone')}</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper', display: { xs: 'none', lg: 'table-cell' } }}>{tl('Registered')}</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>{tl('Status')}</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper', display: { xs: 'none', lg: 'table-cell' } }}>{tl('Wallet')}</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper', width: 56 }} align="right">{tl('Actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => {
              const name = `${row.firstName || ''} ${row.lastName || ''}`.trim() || '—';
              const isDeleted = row.partnerStatus === 'deleted';
              const isBlocked = row.isBlocked;

              return (
                <TableRow
                  key={row._id}
                  hover
                  sx={{ cursor: 'pointer', '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.03) } }}
                  onClick={() => (onView ? onView(row) : onEdit(row))}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={600} color="text.secondary">{serialOffset + index + 1}</Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <Typography variant="body2" fontWeight={700}>{name}</Typography>
                      {row.isVerified && <VerifiedUser color="success" sx={{ fontSize: 16 }} />}
                    </Stack>
                    <Typography variant="caption" color="text.secondary" display="block" noWrap title={row.email || undefined}>
                      {formatEmail(row.email, maskEnabled ?? false)}
                    </Typography>
                    {row.phone && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ display: { sm: 'none' } }}>
                        {formatPhone(row.phone, maskEnabled ?? false)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    <Typography variant="caption" display="block">{formatPhone(row.phone, maskEnabled ?? false)}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">{formatEmail(row.email, maskEnabled ?? false)}</Typography>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    <Typography variant="caption">{row.zoneId?.name || '—'}</Typography>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                    <Typography variant="caption" display="block" fontWeight={600}>
                      {formatRegisteredDate(row.createdAt)}
                    </Typography>
                    {row.createdAt && (
                      <Typography variant="caption" color="text.secondary">
                        {formatRegisteredTime(row.createdAt)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    {isDeleted || isBlocked ? (
                      <Stack spacing={0.35}>
                        <Chip size="small" label={isBlocked ? 'Blocked' : 'Deleted'} color="error" variant="outlined" />
                        {isBlocked && (
                          <Typography variant="caption" color="error.main" sx={{ lineHeight: 1.35 }}>
                            Reason: {(row.blockedReason || '').trim() || 'No reason recorded'}
                          </Typography>
                        )}
                      </Stack>
                    ) : (
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Chip
                          size="small"
                          label={String(row.partnerStatus || 'unknown').replace(/_/g, ' ')}
                          color={STATUS_COLORS[row.partnerStatus || ''] || 'default'}
                          variant="outlined"
                          sx={{ textTransform: 'capitalize' }}
                        />
                        <Switch
                          size="small"
                          checked={!!row.isActive}
                          onChange={(e) => onToggleActive(row, e.target.checked)}
                        />
                      </Stack>
                    )}
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }} onClick={(e) => e.stopPropagation()}>
                    <Stack spacing={0.5}>
                      <BalanceAdjustPopover
                        type="wallet"
                        displayValue={`${formatMoney((row.walletBalance ?? 0))}`}
                        disabled={isDeleted}
                        loading={walletLoading}
                        onRefresh={onRefresh}
                        onApply={(mode, amount, note) => onWalletAdjust(row, mode, amount, note)}
                      />
                      {onWalletView && !isDeleted && (
                        <Typography
                          variant="caption"
                          color="primary"
                          sx={{ cursor: 'pointer', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
                          onClick={() => onWalletView(row)}
                        >
                          View transactions
                        </Typography>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <Stack direction="row" spacing={0.25} justifyContent="flex-end" alignItems="center">
                      {onView && (
                        <Tooltip title="View">
                          <IconButton size="small" color="primary" onClick={() => onView(row)}>
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Actions">
                        <IconButton size="small" onClick={(e) => openMenu(e, row)}>
                          <MoreVert fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {showPagination && (
        <>
          <Divider />
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(_, p) => onPageChange(p)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => onPageSizeChange(parseInt(e.target.value, 10))}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </>
      )}

      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={closeMenu}>
        {onView && (
          <MenuItem onClick={() => { menuRow && onView(menuRow); closeMenu(); }}>
            <ListItemIcon><Visibility fontSize="small" /></ListItemIcon>
            <ListItemText>View</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={() => { menuRow && onEdit(menuRow); closeMenu(); }}>
          <ListItemIcon><Edit fontSize="small" /></ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { menuRow && onDocuments(menuRow); closeMenu(); }}>
          <ListItemIcon><Description fontSize="small" /></ListItemIcon>
          <ListItemText>Documents{menuRow?.pendingDocCount ? ` (${menuRow.pendingDocCount})` : ''}</ListItemText>
        </MenuItem>
        {onProfile && (
          <MenuItem onClick={() => { menuRow && onProfile(menuRow); closeMenu(); }}>
            <ListItemIcon><VerifiedUser fontSize="small" /></ListItemIcon>
            <ListItemText>Provider Profile</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={() => { menuRow && onReferrals(menuRow); closeMenu(); }}>
          <ListItemIcon><CardGiftcard fontSize="small" /></ListItemIcon>
          <ListItemText>Referrals ({menuRow?.referralCount ?? 0})</ListItemText>
        </MenuItem>
        {onWalletView && (
          <MenuItem onClick={() => { menuRow && onWalletView(menuRow); closeMenu(); }}>
            <ListItemIcon><AccountBalanceWallet fontSize="small" /></ListItemIcon>
            <ListItemText>Wallet transactions</ListItemText>
          </MenuItem>
        )}
        {menuRow?.isBlocked ? (
          <MenuItem onClick={() => { menuRow && onUnblock(menuRow); closeMenu(); }}>
            <ListItemIcon><LockOpen fontSize="small" /></ListItemIcon>
            <ListItemText>Unblock</ListItemText>
          </MenuItem>
        ) : menuRow?.partnerStatus !== 'deleted' && (
          <MenuItem onClick={() => { menuRow && onBlock(menuRow); closeMenu(); }}>
            <ListItemIcon><Block fontSize="small" /></ListItemIcon>
            <ListItemText>Block</ListItemText>
          </MenuItem>
        )}
        {menuRow?.partnerStatus !== 'deleted' && (
          <MenuItem onClick={() => { menuRow && onDelete(menuRow); closeMenu(); }} sx={{ color: 'error.main' }}>
            <ListItemIcon><Delete fontSize="small" color="error" /></ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </Paper>
  );
}
